import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { User, Mail, Shield, KeyRound, CheckCircle2, Eye, EyeOff, Phone, MapPin, Save, Lock } from "lucide-react";

const ROLE_META = {
    super_admin: { label: "Super Admin", color: "#0A0A0A" },
    sales: { label: "Sales · Executive", color: "#16A34A" },
    customer: { label: "Customer", color: "#0A0A0A" },
};

export default function Profile() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const roleMeta = ROLE_META[user?.role] || ROLE_META.customer;
    const isCustomer = user?.role === "customer";

    // ── Customer-only profile state ─────────────────────────────────────────
    const [profile, setProfile] = useState(null);
    const [profLoading, setProfLoading] = useState(isCustomer);
    const [profSaving, setProfSaving] = useState(false);
    const [pForm, setPForm] = useState({ name: "", email: "", phone: "", address: "" });

    useEffect(() => {
        if (!isCustomer) return;
        api.get("/customer/profile")
            .then(r => {
                setProfile(r.data);
                setPForm({
                    name: r.data.name || "",
                    email: r.data.email || "",
                    phone: r.data.phone || "",
                    address: r.data.address || "",
                });
            })
            .catch(() => {})
            .finally(() => setProfLoading(false));
    }, [isCustomer]);

    const meetsPolicy = next.length >= 8 && /\d/.test(next) && /[A-Za-z]/.test(next);
    const canSubmit = current && next && confirm && next === confirm && meetsPolicy;

    const submitPassword = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated successfully", { duration: 6000 });
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const submitProfile = async (e) => {
        e.preventDefault();
        setProfSaving(true);
        try {
            const payload = {
                name: pForm.name.trim() || null,
                email: pForm.email.trim().toLowerCase() || null,
                phone: pForm.phone.trim() || null,
                address: pForm.address.trim() || null,
            };
            const { data } = await api.patch("/customer/profile", payload);
            setProfile(data);
            setPForm({
                name: data.name || "",
                email: data.email || "",
                phone: data.phone || "",
                address: data.address || "",
            });
            toast.success("Your details are saved — RPPL Solar admin will see them right away.");
            // If email changed, notify user to log in again next time.
            if (payload.email && payload.email !== user.email.toLowerCase()) {
                toast.info("Your login email has changed. Use the new email next time you sign in.", { duration: 8000 });
            }
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Update failed");
        } finally { setProfSaving(false); }
    };

    const contactLocked = profile?.plants?.some(p => p.updated_by_customer);

    return (
        <div className="space-y-6 max-w-3xl" data-testid="profile-page">
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Account</div>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
                    <User className="w-6 h-6 text-[#0A0A0A]" /> My Profile
                </h1>
                <p className="text-sm text-zinc-500 mt-1">Your account details and security settings.</p>
            </div>

            {/* Identity */}
            <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="profile-identity">
                <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-sm flex items-center justify-center" style={{ background: `${roleMeta.color}18`, border: `1px solid ${roleMeta.color}44` }}>
                        <User className="w-6 h-6" style={{ color: roleMeta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-lg font-semibold text-zinc-900">{profile?.name || user?.name}</div>
                        <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1 font-mono truncate">
                            <Mail className="w-3.5 h-3.5" /> {profile?.email || user?.email}
                        </div>
                        <div className="inline-flex items-center gap-1.5 text-xs font-mono mt-3 px-2 py-0.5 rounded-full"
                            style={{ background: `${roleMeta.color}18`, color: roleMeta.color, border: `1px solid ${roleMeta.color}44` }}>
                            <Shield className="w-3 h-3" /> {roleMeta.label}
                        </div>
                    </div>
                </div>
            </div>

            {/* Personal Details — customer only */}
            {isCustomer && (
                <form onSubmit={submitProfile} className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5" data-testid="customer-profile-form">
                    <div className="flex items-start justify-between gap-2 pb-2 border-b border-zinc-100">
                        <div>
                            <div className="text-sm font-semibold text-zinc-900">Personal Details</div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                                Your entries here are the primary source of truth. Once you save, RPPL Solar admin sees these details and can no longer overwrite them.
                            </div>
                        </div>
                        {contactLocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm bg-green-50 text-green-700 border border-green-200 flex-shrink-0" data-testid="profile-locked-badge">
                                <Lock className="w-3 h-3" /> LOCKED FOR ADMIN
                            </span>
                        )}
                    </div>
                    {profLoading ? (
                        <div className="text-sm text-zinc-500">Loading your details…</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Full Name" icon={User}>
                                    <Input
                                        value={pForm.name}
                                        onChange={e => setPForm(f => ({ ...f, name: e.target.value }))}
                                        className="rounded-sm"
                                        data-testid="profile-name-input"
                                        placeholder="Your name"
                                    />
                                </Field>
                                <Field label="Contact Email" icon={Mail} hint="Also used for login">
                                    <Input
                                        type="email"
                                        value={pForm.email}
                                        onChange={e => setPForm(f => ({ ...f, email: e.target.value }))}
                                        className="rounded-sm font-mono"
                                        data-testid="profile-email-input"
                                        placeholder="you@example.com"
                                    />
                                </Field>
                                <Field label="Mobile Number" icon={Phone}>
                                    <Input
                                        value={pForm.phone}
                                        onChange={e => setPForm(f => ({ ...f, phone: e.target.value }))}
                                        className="rounded-sm font-mono"
                                        data-testid="profile-phone-input"
                                        placeholder="+91 98450 12345"
                                    />
                                </Field>
                                <Field label="" >
                                    <div className="text-[10px] text-zinc-400 mt-2 sm:mt-6 font-mono">
                                        {profile?.plants?.length ? `Linked to ${profile.plants.length} plant${profile.plants.length !== 1 ? "s" : ""}` : "No plants linked"}
                                    </div>
                                </Field>
                            </div>
                            <Field label="Address" icon={MapPin}>
                                <Textarea
                                    value={pForm.address}
                                    onChange={e => setPForm(f => ({ ...f, address: e.target.value }))}
                                    rows={2}
                                    className="rounded-sm"
                                    data-testid="profile-address-input"
                                    placeholder="Door no., street, area, city, PIN"
                                />
                            </Field>
                            {profile?.plants?.[0]?.updated_at && (
                                <div className="text-[10px] text-zinc-500 font-mono">
                                    Last updated on plant contacts: {new Date(profile.plants[0].updated_at).toLocaleString("en-IN")} by {profile.plants[0].updated_by}
                                </div>
                            )}
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    disabled={profSaving}
                                    className="bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm h-10 px-6"
                                    data-testid="profile-save-btn"
                                >
                                    <Save className="w-4 h-4 mr-2" /> {profSaving ? "Saving…" : "Save Details"}
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            )}

            {/* Change password */}
            <form onSubmit={submitPassword} className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5" data-testid="change-password-form">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-[#0A0A0A]" />
                    <div className="text-sm font-semibold text-zinc-900">Change Password</div>
                </div>

                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Current Password</label>
                    <Input type={show ? "text" : "password"} value={current} onChange={e => setCurrent(e.target.value)} className="rounded-sm font-mono" data-testid="current-password" required />
                </div>
                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">New Password</label>
                    <div className="relative">
                        <Input type={show ? "text" : "password"} value={next} onChange={e => setNext(e.target.value)} className="rounded-sm font-mono pr-10" data-testid="new-password" required />
                        <button type="button" onClick={() => setShow(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-700" data-testid="toggle-show-pw">
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <ul className="mt-2 space-y-1 text-xs font-mono" data-testid="password-policy">
                        <PolicyItem ok={next.length >= 8}>Minimum 8 characters</PolicyItem>
                        <PolicyItem ok={/\d/.test(next)}>Contains at least 1 digit</PolicyItem>
                        <PolicyItem ok={/[A-Za-z]/.test(next)}>Contains at least 1 letter</PolicyItem>
                    </ul>
                </div>
                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Confirm New Password</label>
                    <Input type={show ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} className="rounded-sm font-mono" data-testid="confirm-password" required />
                    {confirm && confirm !== next && (
                        <div className="text-xs text-red-600 mt-1.5 font-mono" data-testid="mismatch-warning">Passwords do not match</div>
                    )}
                </div>
                <div className="flex justify-end">
                    <Button type="submit" disabled={!canSubmit || loading} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10 px-6" data-testid="submit-change-password">
                        {loading ? "Saving…" : "Update Password"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, icon: Icon, hint, children }) {
    return (
        <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5" />} {label} {hint && <span className="text-[10px] font-mono text-zinc-400 normal-case tracking-normal">· {hint}</span>}
            </label>
            {children}
        </div>
    );
}

function PolicyItem({ ok, children }) {
    return (
        <li className="flex items-center gap-2" style={{ color: ok ? "#16A34A" : "#a1a1aa" }}>
            <CheckCircle2 className="w-3.5 h-3.5" style={{ opacity: ok ? 1 : 0.35 }} /> {children}
        </li>
    );
}
